import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarBlank, Plus, ArrowsClockwise, Trash, CaretLeft, CaretRight,
  PencilSimple, LockSimple, LockSimpleOpen, X, Download, Link as LinkIcon, Copy,
} from '@phosphor-icons/react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, addMonths, subMonths, isSameMonth, isSameDay, parseISO, isWithinInterval,
} from 'date-fns';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import schoolsService from '../services/schoolsService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';

const emptyYear = { name: '', start_date: '', end_date: '' };
const emptyEvent = { title: '', event_type: 'event', start_date: '', end_date: '', description: '', academic_year_id: '', is_instructional: true };
const emptyRollover = {
  name: '', start_date: '', end_date: '', carry_students: true, clone_classes: true,
  clone_timetable: true, clone_fee_structures: true,
  terms: [
    { term: 'first', start_date: '', end_date: '' },
    { term: 'second', start_date: '', end_date: '' },
    { term: 'third', start_date: '', end_date: '' }
  ]
};

const EVENT_TYPE_COLORS = {
  event: { bg: '#e0e7ff', text: '#4338ca', label: 'Event' },
  holiday: { bg: '#fee2e2', text: '#b91c1c', label: 'Holiday' },
  examination: { bg: '#fef3c7', text: '#92400e', label: 'Examination' },
  meeting: { bg: '#dcfce7', text: '#166534', label: 'Meeting' },
};

function eventColor(type) {
  return EVENT_TYPE_COLORS[type] || EVENT_TYPE_COLORS.event;
}

function AcademicCalendarPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [years, setYears] = useState([]);
  const [events, setEvents] = useState([]);
  const [terms, setTerms] = useState([]);
  const [yearForm, setYearForm] = useState(emptyYear);
  const [editingYearId, setEditingYearId] = useState(null);
  const [editYearForm, setEditYearForm] = useState(emptyYear);
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [editingEventId, setEditingEventId] = useState(null);
  const [rollover, setRollover] = useState(emptyRollover);
  const [busy, setBusy] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [feedStatus, setFeedStatus] = useState(null);
  const [feedBusy, setFeedBusy] = useState(false);
  const [downloadingIcs, setDownloadingIcs] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, type, confirmLabel, onConfirm }

  const load = async () => {
    const [yearData, eventData, termsData] = await Promise.all([
      schoolsService.listAcademicYears(),
      schoolsService.listCalendarEvents(),
      user?.school_id ? schoolsService.listTerms(user.school_id) : Promise.resolve([])
    ]);
    setYears(yearData || []);
    setEvents(eventData || []);
    setTerms(termsData || []);
  };

  useEffect(() => { load().catch(() => toast({ title: 'Error', description: 'Could not load the academic calendar', variant: 'destructive' })); }, [toast, user?.school_id]);

  useEffect(() => {
    schoolsService.getCalendarFeedToken().then(setFeedStatus).catch(() => {});
  }, []);

  const handleDownloadIcs = async () => {
    setDownloadingIcs(true);
    try {
      const response = await schoolsService.downloadCalendarIcs();
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'academic-calendar.ics';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: 'Could not download calendar', description: error.message, variant: 'destructive' });
    } finally { setDownloadingIcs(false); }
  };

  const handleEnableFeed = async () => {
    setFeedBusy(true);
    try {
      const result = await schoolsService.regenerateCalendarFeedToken();
      setFeedStatus(result);
      toast({ title: feedStatus?.enabled ? 'Calendar feed link rotated — the previous link no longer works' : 'Calendar feed enabled' });
    } catch (error) {
      toast({ title: 'Could not enable calendar feed', description: error.message, variant: 'destructive' });
    } finally { setFeedBusy(false); }
  };

  const handleRevokeFeed = () => {
    setConfirmDialog({
      title: 'Revoke Calendar Feed',
      message: 'Anyone subscribed via Google Calendar/Outlook will stop receiving updates.',
      type: 'warning',
      confirmLabel: 'Revoke',
      onConfirm: async () => {
        setFeedBusy(true);
        try {
          const result = await schoolsService.revokeCalendarFeedToken();
          setFeedStatus(result);
          toast({ title: 'Calendar feed revoked' });
        } catch (error) {
          toast({ title: 'Could not revoke calendar feed', description: error.message, variant: 'destructive' });
        } finally { setFeedBusy(false); }
      },
    });
  };

  const handleCopyFeedUrl = () => {
    if (!feedStatus?.feed_url) return;
    navigator.clipboard.writeText(feedStatus.feed_url);
    toast({ title: 'Feed link copied' });
  };

  const submitYear = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await schoolsService.createAcademicYear(yearForm);
      setYearForm(emptyYear);
      await load();
      toast({ title: 'Academic year created' });
    } catch (error) {
      toast({ title: 'Could not create academic year', description: error.response?.data?.detail || 'Check the dates and try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const startEditYear = (year) => {
    setEditingYearId(year.id);
    setEditYearForm({ name: year.name, start_date: year.start_date, end_date: year.end_date });
  };

  const submitEditYear = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await schoolsService.updateAcademicYear(editingYearId, editYearForm);
      setEditingYearId(null);
      await load();
      toast({ title: 'Academic year updated' });
    } catch (error) {
      toast({ title: 'Could not update academic year', description: error.response?.data?.detail || 'Check the dates and try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const startEditEvent = (event) => {
    setEditingEventId(event.id);
    setEventForm({
      title: event.title,
      event_type: event.event_type,
      start_date: event.start_date,
      end_date: event.end_date,
      description: event.description || '',
      academic_year_id: event.academic_year_id || '',
      is_instructional: event.is_instructional,
    });
  };

  const cancelEventEdit = () => {
    setEditingEventId(null);
    setEventForm(emptyEvent);
  };

  const submitEvent = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...eventForm, academic_year_id: eventForm.academic_year_id || null };
      if (editingEventId) {
        await schoolsService.updateCalendarEvent(editingEventId, payload);
        toast({ title: 'Calendar event updated' });
      } else {
        await schoolsService.createCalendarEvent(payload);
        toast({ title: 'Calendar event added' });
      }
      setEventForm(emptyEvent);
      setEditingEventId(null);
      await load();
    } catch (error) {
      toast({ title: editingEventId ? 'Could not update event' : 'Could not add event', description: error.response?.data?.detail || 'Check the dates and try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const deleteEvent = (event) => {
    setConfirmDialog({
      title: 'Delete Calendar Event',
      message: `Delete "${event.title}"?`,
      type: 'error',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        setBusy(true);
        try {
          await schoolsService.deleteCalendarEvent(event.id);
          if (editingEventId === event.id) cancelEventEdit();
          await load();
          toast({ title: 'Calendar event deleted' });
        } catch (error) {
          toast({ title: 'Could not delete event', description: error.response?.data?.detail || 'Try again', variant: 'destructive' });
        } finally { setBusy(false); }
      },
    });
  };

  const runRollover = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const preview = await schoolsService.previewRollover(rollover);
      setBusy(false);
      setConfirmDialog({
        title: 'Run Academic Year Rollover',
        message: `Create ${preview.target_academic_year} with ${preview.class_count} classes and carry ${preview.active_student_count} active students?`,
        type: 'warning',
        confirmLabel: 'Run Rollover',
        onConfirm: async () => {
          setBusy(true);
          try {
            const result = await schoolsService.applyRollover(rollover);
            setRollover(emptyRollover);
            await load();
            toast({
              title: 'Rollover completed',
              description: `Promoted ${result.students_promoted}, repeated ${result.students_repeated}, graduated ${result.students_graduated}` +
                (result.students_unmatched ? `, ${result.students_unmatched} need manual placement` : '') +
                `. Cloned ${result.classes_cloned} classes, ${result.timetable_entries_cloned} timetable entries, ${result.fee_structures_cloned} fee structures.`,
            });
          } catch (error) {
            toast({ title: 'Rollover could not be completed', description: error.response?.data?.detail || 'Review the dates and try again', variant: 'destructive' });
          } finally { setBusy(false); }
        },
      });
    } catch (error) {
      toast({ title: 'Rollover could not be completed', description: error.response?.data?.detail || 'Review the dates and try again', variant: 'destructive' });
      setBusy(false);
    }
  };

  const updateTerm = (index, field, value) => setRollover(current => ({
    ...current,
    terms: current.terms.map((term, termIndex) => termIndex === index ? { ...term, [field]: value } : term)
  }));

  const handleLockTerm = async (term) => {
    try {
      await schoolsService.lockTerm(user.school_id, term.id);
      await load();
      toast({ title: 'Term locked' });
    } catch (error) {
      toast({ title: 'Could not lock term', description: error.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handleUnlockTerm = async (term) => {
    try {
      await schoolsService.unlockTerm(user.school_id, term.id);
      await load();
      toast({ title: 'Term unlocked' });
    } catch (error) {
      toast({ title: 'Could not unlock term', description: error.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handleCloseTerm = async (term) => {
    try {
      await schoolsService.closeTerm(user.school_id, term.id);
      await load();
      toast({ title: 'Term closed' });
    } catch (error) {
      toast({ title: 'Could not close term', description: error.response?.data?.detail, variant: 'destructive' });
    }
  };

  // ---- Month-grid calendar ----
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = endOfWeek(monthEnd);
  const gridDays = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const event of events) {
      try {
        const start = parseISO(event.start_date);
        const end = parseISO(event.end_date);
        for (const day of gridDays) {
          if (isWithinInterval(day, { start, end }) || isSameDay(day, start) || isSameDay(day, end)) {
            const key = format(day, 'yyyy-MM-dd');
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(event);
          }
        }
      } catch {
        // malformed date on an event — skip rendering it on the grid
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, currentMonth]);

  const selectedDayEvents = selectedDate ? (eventsByDay.get(format(selectedDate, 'yyyy-MM-dd')) || []) : [];

  return (
    <Layout>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-start mb-4">
          <div>
            <p className="text-muted mb-1">School administration</p>
            <h1 className="mb-1"><CalendarBlank className="me-2" size={30} />Academic calendar</h1>
            <p className="text-muted">Manage school years, term dates, holidays, and controlled year rollover.</p>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12">
            <section className="card">
              <div className="card-header"><strong>Calendar sync</strong></div>
              <div className="card-body">
                <div className="row g-4">
                  <div className="col-md-5">
                    <p className="text-muted small mb-2">Download every event as a one-time .ics file to import into any calendar app.</p>
                    <button className="btn btn-outline-primary" onClick={handleDownloadIcs} disabled={downloadingIcs}>
                      <Download size={16} className="me-1" /> {downloadingIcs ? 'Preparing…' : 'Download .ics'}
                    </button>
                  </div>
                  <div className="col-md-7">
                    <p className="text-muted small mb-2">Or subscribe from Google Calendar / Outlook so new events show up automatically.</p>
                    {feedStatus?.enabled ? (
                      <div>
                        <div className="input-group mb-2" style={{ maxWidth: 480 }}>
                          <input type="text" className="form-control form-control-sm" readOnly value={feedStatus.feed_url} onClick={e => e.target.select()} />
                          <button className="btn btn-sm btn-outline-secondary" onClick={handleCopyFeedUrl} title="Copy link"><Copy size={14} /></button>
                        </div>
                        <div className="d-flex gap-2">
                          <button className="btn btn-sm btn-outline-secondary" onClick={handleEnableFeed} disabled={feedBusy}><ArrowsClockwise size={14} className="me-1" />Rotate link</button>
                          <button className="btn btn-sm btn-outline-danger" onClick={handleRevokeFeed} disabled={feedBusy}>Revoke</button>
                        </div>
                      </div>
                    ) : (
                      <button className="btn btn-outline-primary" onClick={handleEnableFeed} disabled={feedBusy}>
                        <LinkIcon size={16} className="me-1" /> {feedBusy ? 'Enabling…' : 'Enable subscription feed'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="col-lg-7">
            <section className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center"><strong>Academic years</strong><span className="badge bg-light text-dark">{years.length}</span></div>
              <div className="card-body">
                <form onSubmit={submitYear} className="row g-2 mb-4">
                  <div className="col-md-4"><input className="form-control" placeholder="2026/2027" value={yearForm.name} onChange={e => setYearForm({ ...yearForm, name: e.target.value })} required /></div>
                  <div className="col-md-3"><input className="form-control" type="date" value={yearForm.start_date} onChange={e => setYearForm({ ...yearForm, start_date: e.target.value })} required /></div>
                  <div className="col-md-3"><input className="form-control" type="date" value={yearForm.end_date} onChange={e => setYearForm({ ...yearForm, end_date: e.target.value })} required /></div>
                  <div className="col-md-2"><button className="btn btn-primary w-100" disabled={busy}><Plus size={18} /> Add</button></div>
                </form>
                {years.length === 0 ? <p className="text-muted">No academic years configured yet.</p> : years.map(year => (
                  <div key={year.id} className="border rounded p-3 mb-2">
                    {editingYearId === year.id ? (
                      <form onSubmit={submitEditYear} className="row g-2 align-items-center">
                        <div className="col-md-4"><input className="form-control" value={editYearForm.name} onChange={e => setEditYearForm({ ...editYearForm, name: e.target.value })} required /></div>
                        <div className="col-md-3"><input className="form-control" type="date" value={editYearForm.start_date} onChange={e => setEditYearForm({ ...editYearForm, start_date: e.target.value })} required /></div>
                        <div className="col-md-3"><input className="form-control" type="date" value={editYearForm.end_date} onChange={e => setEditYearForm({ ...editYearForm, end_date: e.target.value })} required /></div>
                        <div className="col-md-2 d-flex gap-1">
                          <button className="btn btn-sm btn-primary" disabled={busy}>Save</button>
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditingYearId(null)}><X size={14} /></button>
                        </div>
                      </form>
                    ) : (
                      <div className="d-flex justify-content-between align-items-center">
                        <div><strong>{year.name}</strong><div className="small text-muted">{year.start_date} to {year.end_date}</div></div>
                        <div className="d-flex align-items-center gap-2">
                          <span className={`badge ${year.is_current ? 'bg-success' : 'bg-secondary'}`}>{year.is_current ? 'Current' : year.status}</span>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => startEditYear(year)} title="Edit"><PencilSimple size={14} /></button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="col-lg-5">
            <section className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <strong>{editingEventId ? 'Edit calendar event' : 'Add calendar event'}</strong>
                {editingEventId && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelEventEdit}><X size={14} /></button>}
              </div>
              <div className="card-body">
                <form onSubmit={submitEvent} className="row g-3">
                  <div className="col-12"><input className="form-control" placeholder="Event or holiday name" value={eventForm.title} onChange={e => setEventForm({ ...eventForm, title: e.target.value })} required /></div>
                  <div className="col-md-6">
                    <select
                      className="form-select"
                      value={eventForm.event_type}
                      onChange={e => {
                        const event_type = e.target.value;
                        setEventForm(current => ({ ...current, event_type, is_instructional: event_type === 'holiday' ? false : current.is_instructional }));
                      }}
                    >
                      <option value="event">Event</option>
                      <option value="holiday">Holiday</option>
                      <option value="examination">Examination</option>
                      <option value="meeting">Meeting</option>
                    </select>
                  </div>
                  <div className="col-md-6"><select className="form-select" value={eventForm.academic_year_id} onChange={e => setEventForm({ ...eventForm, academic_year_id: e.target.value })}><option value="">All years</option>{years.map(year => <option key={year.id} value={year.id}>{year.name}</option>)}</select></div>
                  <div className="col-md-6"><input className="form-control" type="date" value={eventForm.start_date} onChange={e => setEventForm({ ...eventForm, start_date: e.target.value })} required /></div>
                  <div className="col-md-6"><input className="form-control" type="date" value={eventForm.end_date} onChange={e => setEventForm({ ...eventForm, end_date: e.target.value })} required /></div>
                  <div className="col-12">
                    <label className="d-flex align-items-center gap-2">
                      <input type="checkbox" checked={!eventForm.is_instructional} onChange={e => setEventForm({ ...eventForm, is_instructional: !e.target.checked })} />
                      Non-instruction day (excluded from working-day calculations)
                    </label>
                  </div>
                  <div className="col-12"><textarea className="form-control" rows="2" placeholder="Description (optional)" value={eventForm.description} onChange={e => setEventForm({ ...eventForm, description: e.target.value })} /></div>
                  <div className="col-12"><button className="btn btn-outline-primary" disabled={busy}><Plus size={18} /> {editingEventId ? 'Save changes' : 'Add event'}</button></div>
                </form>
              </div>
            </section>
          </div>

          <div className="col-12">
            <section className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <strong>{format(currentMonth, 'MMMM yyyy')}</strong>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentMonth(m => subMonths(m, 1))}><CaretLeft size={16} /></button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentMonth(new Date())}>Today</button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentMonth(m => addMonths(m, 1))}><CaretRight size={16} /></button>
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex gap-2 mb-3 flex-wrap">
                  {Object.entries(EVENT_TYPE_COLORS).map(([type, color]) => (
                    <span key={type} className="small px-2 py-1 rounded" style={{ background: color.bg, color: color.text }}>{color.label}</span>
                  ))}
                </div>
                <div className="row row-cols-7 g-1 mb-1 text-center small text-muted">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="col">{d}</div>)}
                </div>
                <div className="row row-cols-7 g-1">
                  {gridDays.map(day => {
                    const key = format(day, 'yyyy-MM-dd');
                    const dayEvents = eventsByDay.get(key) || [];
                    const inMonth = isSameMonth(day, currentMonth);
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    return (
                      <div key={key} className="col">
                        <button
                          type="button"
                          onClick={() => setSelectedDate(day)}
                          className="w-100 text-start border rounded p-1"
                          style={{
                            minHeight: 78, cursor: 'pointer',
                            background: isSelected ? '#eff6ff' : inMonth ? 'white' : '#f8fafc',
                            borderColor: isSelected ? '#3b82f6' : '#e2e8f0',
                          }}
                        >
                          <div className={`small ${inMonth ? '' : 'text-muted'}`}>{format(day, 'd')}</div>
                          {dayEvents.slice(0, 2).map(event => {
                            const color = eventColor(event.event_type);
                            return (
                              <div key={event.id} className="small rounded px-1 mt-1 text-truncate" style={{ background: color.bg, color: color.text }}>
                                {event.title}
                              </div>
                            );
                          })}
                          {dayEvents.length > 2 && <div className="small text-muted mt-1">+{dayEvents.length - 2} more</div>}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {selectedDate && (
                  <div className="mt-3 border-top pt-3">
                    <strong>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</strong>
                    {selectedDayEvents.length === 0 ? (
                      <p className="text-muted small mt-2 mb-0">No events on this day.</p>
                    ) : (
                      <div className="mt-2 d-flex flex-column gap-2">
                        {selectedDayEvents.map(event => {
                          const color = eventColor(event.event_type);
                          return (
                            <div key={event.id} className="d-flex justify-content-between align-items-start border rounded p-2">
                              <div>
                                <span className="small rounded px-2 py-1 me-2" style={{ background: color.bg, color: color.text }}>{color.label}</span>
                                <strong>{event.title}</strong>
                                {!event.is_instructional && <span className="badge bg-secondary ms-2">Non-instruction day</span>}
                                <div className="small text-muted">{event.start_date} to {event.end_date}</div>
                                {event.description && <div className="small mt-1">{event.description}</div>}
                              </div>
                              <div className="d-flex gap-1">
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => startEditEvent(event)} title="Edit"><PencilSimple size={14} /></button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => deleteEvent(event)} title="Delete"><Trash size={14} /></button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="col-12">
            <section className="card">
              <div className="card-header d-flex justify-content-between align-items-center"><strong>Academic terms</strong><span className="badge bg-light text-dark">{terms.length}</span></div>
              <div className="card-body">
                {terms.length === 0 ? <p className="text-muted mb-0">No academic terms yet — run a rollover below, or add one from Settings.</p> : (
                  <div className="d-flex flex-column gap-2">
                    {terms.map(term => (
                      <div key={term.id} className="border rounded p-3 d-flex justify-content-between align-items-center">
                        <div>
                          <span className="text-capitalize fw-semibold">{term.term} Term — {term.academic_year}</span>
                          {term.is_current && <span className="badge bg-success ms-2">Current</span>}
                          {term.is_locked && <span className="badge bg-secondary ms-2"><LockSimple size={10} className="me-1" />Locked</span>}
                          <div className="small text-muted">{term.start_date} to {term.end_date}</div>
                        </div>
                        <div className="d-flex gap-2">
                          {term.is_locked ? (
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleUnlockTerm(term)}><LockSimpleOpen size={14} className="me-1" />Unlock</button>
                          ) : (
                            <>
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => handleCloseTerm(term)}>Close</button>
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => handleLockTerm(term)}><LockSimple size={14} className="me-1" />Lock</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="col-12">
            <section className="card border-warning">
              <div className="card-header bg-warning-subtle"><strong><ArrowsClockwise className="me-2" />Year rollover</strong></div>
              <div className="card-body">
                <p className="text-muted">Creates the next academic year and three terms, clones the active class structure, teacher assignments, timetable, and fee structures, promotes active students to their next grade level (using each student's term-3 report card decision, defaulting to promoted), and locks the outgoing year's terms.</p>
                <form onSubmit={runRollover} className="row g-3">
                  <div className="col-md-4"><label className="form-label">New academic year</label><input className="form-control" placeholder="2027/2028" value={rollover.name} onChange={e => setRollover({ ...rollover, name: e.target.value })} required /></div>
                  <div className="col-md-4"><label className="form-label">Year starts</label><input className="form-control" type="date" value={rollover.start_date} onChange={e => setRollover({ ...rollover, start_date: e.target.value })} required /></div>
                  <div className="col-md-4"><label className="form-label">Year ends</label><input className="form-control" type="date" value={rollover.end_date} onChange={e => setRollover({ ...rollover, end_date: e.target.value })} required /></div>
                  {rollover.terms.map((term, index) => <div className="col-md-4" key={term.term}><label className="form-label text-capitalize">{term.term} term</label><div className="d-flex gap-2"><input className="form-control" type="date" value={term.start_date} onChange={e => updateTerm(index, 'start_date', e.target.value)} required /><input className="form-control" type="date" value={term.end_date} onChange={e => updateTerm(index, 'end_date', e.target.value)} required /></div></div>)}
                  <div className="col-12 d-flex gap-4 flex-wrap">
                    <label><input type="checkbox" className="me-2" checked={rollover.clone_classes} onChange={e => setRollover({ ...rollover, clone_classes: e.target.checked })} />Clone classes and assignments</label>
                    <label><input type="checkbox" className="me-2" checked={rollover.clone_timetable} onChange={e => setRollover({ ...rollover, clone_timetable: e.target.checked })} />Clone timetable</label>
                    <label><input type="checkbox" className="me-2" checked={rollover.clone_fee_structures} onChange={e => setRollover({ ...rollover, clone_fee_structures: e.target.checked })} />Clone fee structures</label>
                    <label><input type="checkbox" className="me-2" checked={rollover.carry_students} onChange={e => setRollover({ ...rollover, carry_students: e.target.checked })} />Promote/carry active students</label>
                  </div>
                  <div className="col-12"><button className="btn btn-warning" disabled={busy}><ArrowsClockwise size={18} /> Preview and run rollover</button></div>
                </form>
              </div>
            </section>
          </div>
        </div>
      </div>

      <DialogModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        primaryAction={{ label: confirmDialog?.confirmLabel || 'Confirm', onClick: () => confirmDialog?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default AcademicCalendarPage;
